import * as yup from 'yup'
import type {Conversation} from '@/client/mentor/ConversationsClient'
import {addDefaultErrorToast, addDefaultSuccessToast} from '@/utils/toast'
import {CONVERSATION_TITLE_MAX_CHARS, CONVERSATION_TITLE_MIN_CHARS} from '@/components/assistant/constants'
import {computed, ref} from 'vue'
import {useField, useForm} from 'vee-validate'
import {useI18n} from 'vue-i18n'
import {useModalStore} from '@/stores/modal'
import {useToastStore} from '@/stores/toast'

export function useEditConversation(patchConversationTitle: (id: string, title: string) => Promise<Conversation>) {
  const modalStore = useModalStore()
  const toastStore = useToastStore()
  const {t} = useI18n()

  const editConversationModalId = ref<string>()

  const {meta, resetForm} = useForm({
    initialValues: {
      title: '',
    },
    validationSchema: yup.object({
      title: yup
        .string()
        .required(t('validation.required'))
        .min(CONVERSATION_TITLE_MIN_CHARS, t('validation.min'))
        .max(CONVERSATION_TITLE_MAX_CHARS, t('validation.max')),
    }),
  })

  const {value: editTitle, errorMessage: editTitleError} = useField<string>('title')

  const isEditEnabled = computed(() => meta.value.dirty && meta.value.valid)

  function openEditConversationModal(conversation: Conversation, title: string) {
    resetForm({
      values: {
        title,
      },
    })

    editConversationModalId.value = modalStore.addModal(
      t('assistant.modal.edit.title'),
      undefined,
      {
        callback: async () => await submitConversationUpdate(conversation.id, editTitle.value.trim()),
        enabled: isEditEnabled,
        label: t('assistant.actions.update'),
        variant: 'primary',
      },
      {
        label: t('assistant.actions.cancel'),
        variant: 'warning-text',
      }
    )
  }

  async function submitConversationUpdate(conversationId: string, newTitle: string) {
    try {
      await patchConversationTitle(conversationId, newTitle)
      addDefaultSuccessToast(toastStore, t)
    } catch (error) {
      addDefaultErrorToast(toastStore, t, error)
    }
  }

  return {
    editConversationModalId,
    editTitle,
    editTitleError,
    openEditConversationModal,
  }
}
